import api, { ValidateAxiosError } from '@/modules/api.service';
import { Endpoints } from '@/shared/constants/endpoints';
import { SearchDto } from '@/shared/types/search.dto';

export interface CreateCandidatoDto {
  nome: string;
  sobrenome?: string;
  cpf: string;
  dataNascimento: string;
  genero: 'masculino' | 'feminino';
  email: string;
  emailConfirmacao?: string; // Campo apenas para validação frontend, não é enviado ao backend
  telefone?: {
    ddd?: string;
    numero?: string;
  };
  celular?: {
    ddd?: string;
    numero?: string;
  };
  endereco?: {
    cep?: string;
    logradouro?: string;
    numero?: number;
    complemento?: string;
    bairro?: string;
    cidade?: string;
    estado?: string;
  };
  instituicaoEnsinoId?: number;
  instituicaoEnsinoNome?: string;
  cursoId?: number;
  nomeSocial?: string;
  periodoAtualCurso?: number;
  anoInicioCurso?: number;
  cursoPretendidoId: number;
  comarcaPretendidaId?: number;
  distritoPretendidoId?: number;
  municipio?: string;
  raca: 'branca' | 'preta' | 'parda' | 'amarela' | 'indigena';
  optaVagasNegros?: boolean;
  declaraIndigena?: boolean;
  documentacaoIndigenaUrl?: string;
  pessoaComDeficiencia?: boolean;
  optaVagasPcd?: boolean;
  descricaoDeficiencia?: string;
  especificacaoDeficiencia?: string;
  necessitaCuidadosEspeciais?: boolean;
  laudoMedicoUrl?: string;
  documentoIdentificacaoUrl?: string;
  comprovanteResidenciaUrl?: string;
  documentoEstudoUrl?: string;
  documentosComplementaresUrl?: string;
  declaracaoVeracidade: boolean;
  confirmacaoEdital: boolean;
  consentimentoLgpd: boolean;
  processoSeletivoId: number;
  /** Apenas para envio no endpoint público de inscrição */
  captchaToken?: string;
}

export interface ReadCandidatoDto {
  id: number;
  pessoaId: string;
  pessoaNome?: string;
  pessoaCpf?: string;
  pessoaEmail?: string;
  pessoaTelefone?: string;
  dataNascimento?: string;
  telefone?: {
    ddd?: string;
    numero?: string;
  };
  celular?: {
    ddd?: string;
    numero?: string;
  };
  endereco?: {
    cep?: string;
    logradouro?: string;
    numero?: number;
    complemento?: string;
    bairro?: string;
    cidade?: string;
    estado?: string;
  };
  processoSeletivoId: number;
  instituicaoEnsinoId?: number;
  instituicaoEnsinoNome?: string;
  cursoId?: number;
  nomeSocial?: string;
  periodoAtualCurso?: number;
  anoInicioCurso?: number;
  cursoPretendidoId: number;
  cursoPretendidoNome?: string;
  comarcaPretendidaId: number;
  comarcaPretendidaNome?: string;
  distritoPretendidoId?: number;
  distritoPretendidoNome?: string;
  municipio?: string;
  genero: string;
  raca: string;
  optaVagasNegros?: boolean;
  declaraIndigena?: boolean;
  documentacaoIndigenaUrl?: string;
  pessoaComDeficiencia?: boolean;
  optaVagasPcd?: boolean;
  descricaoDeficiencia?: string;
  especificacaoDeficiencia?: string;
  necessitaCuidadosEspeciais?: boolean;
  laudoMedicoUrl?: string;
  documentoIdentificacaoUrl?: string;
  comprovanteResidenciaUrl?: string;
  documentoEstudoUrl?: string;
  documentosComplementaresUrl?: string;
  declaracaoVeracidade: boolean;
  confirmacaoEdital: boolean;
  consentimentoLgpd: boolean;
  situacao: string;
  dataInscricao: Date;
  criadoEm: Date;
  editadoEm: Date;
}

export type UpdateCandidatoDto = Partial<CreateCandidatoDto>;

export class CandidatoService {
  async create(obj: CreateCandidatoDto): Promise<number> {
    try {
      return (await api.post(Endpoints.CANDIDATO, obj)).data;
    } catch (error) {
      return ValidateAxiosError(error, 'Falha ao realizar inscrição.');
    }
  }

  async createPublic(obj: CreateCandidatoDto): Promise<number> {
    try {
      return (await api.post(Endpoints.CANDIDATO + '/public', obj)).data;
    } catch (error: any) {
      
      // Se o erro contém uma lista de erros de validação, lançar o erro completo sem processar
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors) && error.response.data.errors.length > 0) {
        throw error; // Lançar o erro para ser processado no componente
      }
      // Para outros erros, usar ValidateAxiosError
      throw ValidateAxiosError(error, 'Falha ao realizar inscrição.');
    }
  }

  async findOne(id: number): Promise<ReadCandidatoDto> {
    try {
      const response = await api.get(Endpoints.CANDIDATO + '/' + id);
      return response.data;
    } catch (error) {
      return ValidateAxiosError(error, 'Falha ao buscar a inscrição.');
    }
  }

  async findOnePublic(id: number): Promise<ReadCandidatoDto> {
    try {
      const response = await api.get(Endpoints.CANDIDATO + '/public/' + id);
      return response.data;
    } catch (error) {
      return ValidateAxiosError(error, 'Falha ao buscar a inscrição.');
    }
  }

  async update(id: number, dto: UpdateCandidatoDto): Promise<void> {
    try {
      return (await api.put(Endpoints.CANDIDATO + '/' + id, dto)).data;
    } catch (error) {
      return ValidateAxiosError(error, 'Falha ao atualizar inscrição.');
    }
  }

  async updatePublic(id: number, dto: UpdateCandidatoDto): Promise<void> {
    try {
      return (await api.put(Endpoints.CANDIDATO + '/public/' + id, dto)).data;
    } catch (error: any) {
      // Se o erro contém uma lista de erros de validação, lançar o erro completo sem processar
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        throw error; // Lançar o erro para ser processado no componente
      }
      // Para outros erros, usar ValidateAxiosError
      throw ValidateAxiosError(error, 'Falha ao atualizar inscrição.');
    }
  }

  async findAll(conditions: SearchDto, processoSeletivoId?: number): Promise<[ReadCandidatoDto[], number]> {
    try {
      const params: any = { skip: conditions.skip, take: conditions.take };
      if (processoSeletivoId) {
        params.processoSeletivoId = processoSeletivoId;
      }
      const response = await api.get(Endpoints.CANDIDATO, { params });
      return response.data;
    } catch (error) {
      return ValidateAxiosError(error, 'Falha ao listar candidatos.');
    }
  }

  async uploadFile(file: File, tipo: 'documentacao_indigena' | 'laudo_medico' | 'documento_identificacao' | 'comprovante_residencia' | 'documento_estudo' | 'documentos_complementares', candidatoId?: number): Promise<string> {
    try {
      if (!file) {
        throw new Error('Arquivo não fornecido');
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('tipo', tipo);
      if (candidatoId) {
        formData.append('candidatoId', candidatoId.toString());
      }

      // NÃO definir Content-Type manualmente - o navegador precisa definir com boundary
      const response = await api.post(Endpoints.CANDIDATO + '/upload', formData);
      
      if (!response.data?.fileId) {
        throw new Error('Resposta do servidor não contém fileId');
      }
      
      return response.data.fileId;
    } catch (error: any) {
      console.error('Erro no upload:', error);
      if (error.response) {
        console.error('Resposta do erro:', error.response.data);
        console.error('Status:', error.response.status);
      }
      ValidateAxiosError(error, 'Falha ao fazer upload do arquivo.');
      throw error;
    }
  }

  async downloadFile(fileId: string): Promise<Blob> {
    try {
      const response = await api.get(Endpoints.CANDIDATO + '/file/' + fileId, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      return ValidateAxiosError(error, 'Falha ao baixar arquivo.');
    }
  }

  async delete(id: number): Promise<void> {
    try {
      await api.delete(Endpoints.CANDIDATO + '/' + id);
    } catch (error) {
      return ValidateAxiosError(error, 'Falha ao excluir candidato.');
    }
  }
}

